// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Container } from './container';
import { Component } from './component';
import { Button } from './button';
import { render, getComponent } from './common';

/**
 * @class Jsw.Bar
 * @extends Jsw.Container
 */
export const Bar = Class.create(Container, {
    /**
     * @cfg {String} type=static Type of tabs (static or dynamic)
     */

    _initConfiguration($super, config) {
        $super(config);

        this._contentAreaId = `${this._id}-content-area`;
        this._type = this._getConfigParam('type', 'static');
    },

    _initComponentElement($super) {
        $super();

        let content = `<ul id="${this._contentAreaId}"></ul>`;
        if ('navbar' !== this._id) {
            content = `<div class="tabs-area">${content}</div>`;
        }

        this._updateComponentElement(content);
    },

    _renderItem(item) {
        const renderTargetId = `${this._id}-item-${item.getId()}`;
        const containerCls = item.getContainerClass();

        $(this._contentAreaId).insert(`<li class="${containerCls}" id="${renderTargetId}"></li>`);
        render($(renderTargetId), item);

        if ('dynamic' === this._type) {
            item.addEventObserver('click', item.switchTab.bindAsEventListener(item));
        }

        if ($('main')) {
            if (!$('main').hasClassName('tabed') && 'navbar' !== this._id) {
                $('main').addClassName('tabed');
            }
        }
    },
});

/**
 * @class Jsw.bar.Tabs
 * @extends Jsw.Bar
 *
 * The following example demonstrates how to use static tabs:
 *
 *     @example
 *     new Jsw.bar.Tabs({
 *         renderTo: document.body,
 *         cls: 'tabs',
 *         items: [{
 *             componentType: 'Jsw.bar.Button',
 *             title: 'Tab 1',
 *             active: true,
 *             href: 'http://example1.com'
 *         }, {
 *             componentType: 'Jsw.bar.Button',
 *             title: 'Tab 2',
 *             href: 'http://example2.com'
 *         }]
 *     });
 *
 * The following example demonstrates how to use dynamic tabs:
 *
 *     @example
 *     new Jsw.bar.Tabs({
 *         renderTo: document.body,
 *         cls: 'tabs',
 *         type: 'dynamic',
 *         items: [{
 *             componentType: 'Jsw.bar.Button',
 *             tabId: 'tab-1',
 *             title: 'Tab 1',
 *             active: true,
 *             content: 'Content of Tab 1'
 *         }, {
 *             componentType: 'Jsw.bar.Button',
 *             tabId: 'tab-2',
 *             title: 'Tab 2',
 *             content: new Jsw.Box({
 *                 html: 'Content of Tab 2'
 *             })
 *         }]
 *     });

 */
export const bar = {};
bar.Tabs = Class.create(Bar, {
    /**
     * @cfg {Boolean} responsive=true
     */

    _initConfiguration($super, config) {
        $super(config);
        this._isResponsive = this._getConfigParam('responsive', true);
    },

    _renderItems($super) {
        $super();

        if (this._isResponsive) {
            this._addResponsiveTabs();
        }
    },

    _addResponsiveTabs() {
        const element = $(this._id);
        element.select('li a').each(function (el) {
            el.observe('click', function (event) {
                const listItem = el.up('li');
                if (listItem.hasClassName('active') || listItem.id === 'current') {
                    Event.stop(event);
                }
                element.toggleClassName('responsive-tabs-visible');
            });
        });
    },

    switchTab(tabId) {
        let activeTab = null;

        this.getItems().each(function (item) {
            if (tabId === item.getTabId()) {
                activeTab = item;
            }

            $(item.getTabId()).hide();
            item.getRenderTarget().removeClassName('active');
        });

        $(tabId).show();
        activeTab.getRenderTarget().addClassName('active');
    },

    _renderItem($super, item, i, size) {
        $super(item, i, size);

        const content = item.getTabContent();

        if (content) {
            const tabContentElement = new Element('div', {
                id: item.getTabId(),
            });

            if (!item.isActive()) {
                tabContentElement.setStyle({ display: 'none' });
            }

            this._componentElement.up().insert({ bottom: tabContentElement });

            if (Object.isString(content)) {
                tabContentElement.update(content);
            } else {
                render($(item.getTabId()), content);
            }
        }
    },
});

/**
 * @class Jsw.bar.Button
 * @extends Jsw.Button
 */
bar.Button = Class.create(Button, {
    /**
     * @cfg {Boolean} active=false
     */
    /**
     * @cfg {String} containerCls
     */
    /**
     * @cfg {String} tabId
     */
    /**
     * @cfg {Boolean} navigationTab=false
     */
    /**
     * @cfg {String|Jsw.Component} content
     */

    _initConfiguration($super, config) {
        $super(config);

        this._isActive = this._getConfigParam('active', false);
        this._cls = this._getConfigParam('cls', '');
        this._containerCls = this._getConfigParam('containerCls', this._isActive ? 'active' : '');
        this._tabId = this._getConfigParam('tabId', null);
        if (this._tabId) {
            this._id = `${this._tabId}-button`;
        }
        this.navigationTab = this._getConfigParam('navigationTab', false);
        this._content = this._getConfigParam('content', null);
    },

    _initComponentElement($super) {
        $super();

        this._componentElement.update(`<span>${this._title.escapeHTML()}</span>`);
    },

    getContainerClass() {
        return this._containerCls;
    },

    getTabId() {
        return this._tabId;
    },

    switchTab(event) {
        const bar = getComponent(Event.element(event).up('ul').up('div')
            .up('div').id);
        bar.switchTab(this._tabId);
        Event.stop(event);
    },

    getTabContent() {
        return this._content;
    },

    isActive() {
        return this._isActive;
    },
});

/**
 * @class Jsw.bar.Separator
 * @extends Jsw.Component
 */
bar.Separator = Class.create(Component, {
    _tag: 'span',

    _initConfiguration($super, config) {
        $super(config);
        this._cls = this._getConfigParam('cls', 'separator');
    },

    _initComponentElement($super) {
        $super();
        this._componentElement.update('<span></span>');
    },
});

/**
 * @class Jsw.bar.HorizontalSeparator
 * @extends Jsw.Component
 */
bar.HorizontalSeparator = Class.create(Component, {
    _tag: 'span',

    _initConfiguration($super, config) {
        $super(config);
        this._wrapperClass = this._getConfigParam('wrapperClass', 'separator');
    },
});



// WEBPACK FOOTER //
// ./jsw/bar.js